/* eslint-disable @typescript-eslint/no-magic-numbers */

interface Token {
  type: string;
  value: string[];
  position: {
    line: number;
    index: number;
  };
}

const parseMarkdownUtil = (markdown: string): string => {
  const tokens: Token[] = [];
  const outputLines: string[] = [];

  // Regular expressions for various Markdown elements
  const rules = [
    { type: 'header', regex: /^(#+)(.*)/ },
    { type: 'boldLink', regex: /\[(\*\*|__)(.*?)\1\]\((.*?)\)/ },
    { type: 'bold', regex: /(\*\*|__)(.*?)\1/ },
    { type: 'italic', regex: /(\*|_)(.*?)\1/ },
    { type: 'link', regex: /\[(.*?)\]\((.*?)\)/ },
    { type: 'list', regex: /^(\s*)(\d+\.|\*|\-)(\s*)(.*)/ },
  ];

  // Tokenize the Markdown string
  const tokenize = (line: string, lineIndex: number) => {
    let index = 0;
    let match;

    while (index < rules.length) {
      const rule = rules[index];
      match = rule.regex.exec(line);

      if (match) {
        tokens.push({ type: rule.type, value: match, position: { line: lineIndex, index: tokens.length } });
        line = line.replace(match[0], `&&${tokens.length - 1}&&`);

        // Reset the index to check all rules again
        index = 0;
      } else {
        index++;
      }
    }

    if (!match) {
      tokens.push({ type: 'text', value: [line], position: { line: lineIndex, index: tokens.length } });
    }

    return line;
  };

  // Parse tokens into HTML
  const parseTokens = (token: Token) => {
    switch (token.type) {
      case 'header':
        const level = token.value[1].length;

        return `<h${level}>${token.value[2].trim()}</h${level}>`;
      case 'bold':
        return `<b>${token.value[2]}</b>`;
      case 'italic':
        return `<i>${token.value[2]}</i>`;
      case 'link':
        return `<a class="md-link" href="${token.value[2]}">${token.value[1]}</a>`;
      case 'boldLink':
        return `<a class="md-link" href="${token.value[3]}"><b>${token.value[2]}</b></a>`;
      case 'list':
        const indent = token.value[1].length;
        const listType = token.value[2].trim() === '-' ? 'ul' : 'ol';

        return `<${listType} class="md-list md-list--${listType}"></listType><li class="md-list-item" style="margin-left: ${
          indent * 20
        }px">${token.value[4]}</li></${listType}>`;
      default:
        return token.value[0];
    }
  };

  const parseLine = (line: string, lineIndex: number): string => {
    // Look for &&N&& in the line
    const match = /&&(\d+)&&/.exec(line);
    if (match) {
      // Replace &&N&& with the corresponding token
      const index = parseInt(match[1], 10);
      const token = tokens.find((t) => t.position.index === index && t.position.line === lineIndex);

      if (!token) {
        return line;
      }

      line = line.replace(/&&\d+&&/, parseTokens(token));

      return parseLine(line, lineIndex); // Call parseLine recursively
    }

    return line;
  };

  // Process each line of the Markdown string
  const lines = markdown.split(/\r?\n/);
  lines.forEach((line, index) => {
    const outputLine = tokenize(line, index);
    outputLines.push(outputLine);
  });

  const outputHtml = outputLines.map(parseLine);

  return outputHtml.join('<br />');
};

export default parseMarkdownUtil;
